import { useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import { isAxiosError } from 'axios';
import { getFinanceAuditHistory, getFinanceStudent } from '../../api/finance';
import {
  downloadInvoicePdf,
  getStudentInvoicePdfBlob,
  listStudentInvoices,
} from '../../api/students';
import {
  DocumentPreviewDialog,
  DocumentPreviewTarget,
} from '../../components/DocumentPreviewDialog';
import { downloadBlob } from '../../utils/downloadBlob';
import { StudentPaymentsTab } from '../students/StudentPaymentsTab';
import { describeFinanceEvent } from './financeAudit';
import {
  formatDate,
  formatMoney,
  PAYMENT_STATUS_COLOR,
  PAYMENT_STATUS_LABEL,
} from './financeFormat';
import { NumberLabel } from '../../components/NumberLabel';

/** Finance → Student Payments → one student (Phase 96): identity,
 * programme and Payment Checklist balance, then the same payments panel
 * as the student profile (checklist, fee breakdown, record/confirm, slips,
 * invoices, receipts, ledger), invoice history and finance audit history.
 * Nothing personal (contact, passport, family, documents, notes,
 * signature) is fetched. */
export const FinanceStudentPage = () => {
  const { id } = useParams();
  const studentId = Number(id);
  const [searchParams] = useSearchParams();
  const highlightPaymentId = searchParams.get('paymentId')
    ? Number(searchParams.get('paymentId'))
    : null;
  const [previewTarget, setPreviewTarget] = useState<DocumentPreviewTarget | null>(null);

  const studentQuery = useQuery({
    queryKey: ['finance-student', studentId],
    queryFn: () => getFinanceStudent(studentId),
    enabled: Number.isInteger(studentId),
    retry: (count, err) => !(isAxiosError(err) && err.response?.status === 404) && count < 2,
  });
  const invoicesQuery = useQuery({
    queryKey: ['student-invoices', studentId],
    queryFn: () => listStudentInvoices(studentId),
    enabled: studentQuery.isSuccess,
  });
  const auditQuery = useQuery({
    queryKey: ['finance-audit', studentId],
    queryFn: () => getFinanceAuditHistory(studentId),
    enabled: studentQuery.isSuccess,
  });

  if (studentQuery.isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (studentQuery.isError || !studentQuery.data) {
    const notFound =
      isAxiosError(studentQuery.error) && studentQuery.error.response?.status === 404;
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={notFound ? 'warning' : 'error'}>
          {notFound
            ? "This student doesn't exist, or isn't in your branch."
            : "Couldn't load this student's finance details."}
        </Alert>
        <Button component={RouterLink} to="/finance/students" sx={{ mt: 2 }}>
          Back to Student Payments
        </Button>
      </Box>
    );
  }

  const s = studentQuery.data;
  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/finance/students" underline="hover" color="inherit">
          Student Payments
        </Link>
        <Typography color="text.primary">{s.name}</Typography>
      </Breadcrumbs>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {s.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <NumberLabel value={s.studentNumber} kind="student" variant="inherit" /> ·{' '}
                {s.branch?.name ?? 'No branch'}
                {s.isDraft ? ' · registration in progress' : ''}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {[s.programme.university, s.programme.course, s.programme.intake]
                  .filter(Boolean)
                  .join(' — ') || 'No programme yet'}
                {s.programme.programmeSource === 'REGISTRATION' ? ' (chosen at registration)' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Registered by {s.registeredBy ?? '—'}
              </Typography>
              {s.applications.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  Other applications:{' '}
                  {s.applications
                    .slice(1)
                    .map(
                      (a) =>
                        `${a.applicationNumber} (${a.university ?? '—'}, ${a.status.replace(/_/g, ' ').toLowerCase()})`,
                    )
                    .join('; ')}
                </Typography>
              )}
            </Box>
            <Stack spacing={0.5} alignItems={{ md: 'flex-end' }}>
              <Chip
                color={PAYMENT_STATUS_COLOR[s.finance.status]}
                label={PAYMENT_STATUS_LABEL[s.finance.status]}
                sx={{ alignSelf: { md: 'flex-end' } }}
              />
              <Typography variant="body2">
                Required: {formatMoney(s.finance.required, s.finance.currency)}
              </Typography>
              <Typography variant="body2">
                Paid: {formatMoney(s.finance.paid, s.finance.currency)}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                Outstanding: {formatMoney(s.finance.outstanding, s.finance.currency)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <StudentPaymentsTab
        studentId={studentId}
        highlightPaymentId={highlightPaymentId}
        lookupSource="finance"
      />

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Invoice history
          </Typography>
          {invoicesQuery.isError && <Alert severity="error">Couldn't load invoices.</Alert>}
          {invoicesQuery.data?.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No invoices generated yet. Use the Payment Checklist above to generate a USD or LKR
              invoice.
            </Typography>
          )}
          {!!invoicesQuery.data?.length && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Issued</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Issued by</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoicesQuery.data.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.invoiceNumber}</TableCell>
                      <TableCell>{formatDate(inv.issuedAt)}</TableCell>
                      <TableCell>{inv.invoiceType ?? '—'}</TableCell>
                      <TableCell align="right">
                        {formatMoney(Number(inv.amount), inv.currency)}
                      </TableCell>
                      <TableCell>
                        {inv.issuedBy.firstName} {inv.issuedBy.lastName}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<DescriptionIcon />}
                          onClick={() =>
                            setPreviewTarget({
                              key: `inv-${inv.id}`,
                              originalName: `${inv.invoiceNumber}.pdf`,
                              mimeType: 'application/pdf',
                              fetchBlob: () => getStudentInvoicePdfBlob(studentId, inv.id),
                              onDownload: async () =>
                                downloadBlob(
                                  await downloadInvoicePdf(studentId, inv.id),
                                  `${inv.invoiceNumber}.pdf`,
                                ),
                            })
                          }
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={async () =>
                            downloadBlob(
                              await downloadInvoicePdf(studentId, inv.id),
                              `${inv.invoiceNumber}.pdf`,
                            )
                          }
                        >
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Finance audit history
          </Typography>
          {auditQuery.isError && <Alert severity="error">Couldn't load the audit history.</Alert>}
          {auditQuery.data?.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No finance activity recorded yet.
            </Typography>
          )}
          <Stack spacing={1}>
            {auditQuery.data?.map((e) => (
              <Stack key={e.id} direction="row" spacing={2} alignItems="baseline">
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
                  {new Date(e.createdAt).toLocaleString('en-GB', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {describeFinanceEvent(e)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {e.user?.name ?? 'System'}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <DocumentPreviewDialog target={previewTarget} onClose={() => setPreviewTarget(null)} />
    </Box>
  );
};
